import React, { useState, useMemo, useEffect } from 'react';
import {
  Button,
  Container,
  Grid,
  Box,
  Tooltip,
} from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import query_data from '../../js/query_data';
import { convertEpochToDateTime } from '../../js/time_helpers';
import { getMuiTableBodyProps, getTableTheme } from '../../js/table_theme_config';

// A simple component to render a status dot
const StatusDot = ({ active }) => (
  <Tooltip title={active ? 'Active' : 'Inactive'}>
    <Box
      sx={{
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: active ? 'green' : 'red',
        display: 'inline-block',
        verticalAlign: 'middle',
      }}
    />
  </Tooltip>
);

const AgentsTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // This function now groups the flat agent list by robot_name
  const process_data = async (agentList) => {
    const fiveMinutesInMs = 5 * 60 * 1000;
    const nowMs = Date.now();

    // Helper to format epoch nanoseconds
    const formatDT = (epochNs) => {
      if (!epochNs) return 'N/A';
      const dtObj = convertEpochToDateTime(epochNs);
      return dtObj ? `${dtObj.date} ${dtObj.time}` : 'Invalid Date';
    };

    // 1. Group agents by robot_name (now nested under agent.robot)
    const agentsByRobot = agentList.reduce((acc, agent) => {
      const robot_name = agent.robot?.robot_name || 'Unknown';
      if (!acc[robot_name]) {
        acc[robot_name] = [];
      }
      acc[robot_name].push(agent);
      return acc;
    }, {});

    // 2. Create nested structure for the table
    const processedData = Object.keys(agentsByRobot).map(robot_name => {
      const agents = agentsByRobot[robot_name];
      let latestHeartbeat = 0;
      
      // 3. Create sub-rows for each individual agent
      const subRows = agents.map(agent => {
        const heartbeatMs = agent.last_heartbeat_epoch_ns / 1000000; // Convert ns to ms
        const isActive = (nowMs - heartbeatMs) < fiveMinutesInMs;
        if (heartbeatMs > latestHeartbeat) {
          latestHeartbeat = heartbeatMs;
        }
        return {
          id: agent.id,
          hostname: agent.hostname,
          status: isActive,
          last_heartbeat: formatDT(agent.last_heartbeat_epoch_ns),
          created_at: formatDT(agent.created_at_epoch_ns),
        };
      });

      // 4. Determine the parent row's aggregated status
      const isRobotActive = subRows.some(agent => agent.status);

      // 5. Create the main parent row for the robot
      // Convert latestHeartbeat back to ns for formatDT
      const latestHeartbeatNs = latestHeartbeat * 1000000;
      return {
        robot_name,
        hostname: `${subRows.length} agent(s)`, // Display agent count in hostname column for parent
        status: isRobotActive,
        last_heartbeat: formatDT(latestHeartbeatNs), // Show the most recent heartbeat for the robot
        subRows,
      };
    });

    return processedData;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await query_data("/agents", {});
      const processedData = await process_data(response.data);
      setData(processedData);
    } catch (error) {
      console.error("Failed to fetch agent data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Columns are defined to work with the parent/sub-row structure
  const columns = useMemo(() => [
    {
      accessorKey: 'status',
      header: 'Status',
      size: 80,
      Cell: ({ cell }) => <StatusDot active={cell.getValue()} />,
    },
    { accessorKey: 'robot_name', header: 'Robot Name' },
    { accessorKey: 'hostname', header: 'Hostname / Agent Count' }, // Represents hostname for agent, count for robot
    { accessorKey: 'last_heartbeat', header: 'Last Heartbeat' },
    { accessorKey: 'created_at', header: 'First Seen' }, // Only appears for sub-rows
  ], []);

  const table = useMaterialReactTable({
    columns,
    data,
    state: { isLoading: loading },
    // Enable features for expanding and filtering grouped rows
    enableExpanding: true,
    filterFromLeafRows: true,
    getRowId: (row) => row.robot_name, // Use the unique robot_name for the parent row ID
    muiTableBodyProps: getMuiTableBodyProps(false),
    mrtTheme: getTableTheme(false),
    renderTopToolbarCustomActions: () => (
      <Grid container justifyContent="flex-start">
        <Button onClick={fetchData} variant="outlined">Refresh</Button>
      </Grid>
    ),
  });

  return (
    <Container maxWidth={false} disableGutters sx={{ pt: 2, width: '100%' }}>
      <MaterialReactTable table={table} />
    </Container>
  );
};

export default AgentsTable;